import React from 'react';
import { TemplateProps } from '../../../types/cv';
import { Mail, Phone, MapPin, Globe } from 'lucide-react';
import { CV_LABELS, frPresent } from '../../../utils/cvLabels';
import { isSectionContinuation } from '../../../utils/cvPagination';
import SectionHeading from '../SectionHeading';

/** Corporate Abidjan — bandeau marine + accent orange CI. */
const AbidjanCorporateTemplate: React.FC<TemplateProps> = ({
    data,
    isContinuationPage = false,
    showPlaceholders = false,
    pageIndex = 0,
    fullData,
}) => {
    const primaryColor = data.style?.primaryColor || '#1e3a5f';
    const accent = '#ea580c';
    const {
        fontFamily = 'font-sans',
        textColor = '#0f172a',
        backgroundColor = '#ffffff',
        sectionBackgroundColor = '#f8fafc',
    } = data.style || {};

    const source = fullData || data;
    const eduContinued = isSectionContinuation(source, 'education', pageIndex);
    const expContinued = isSectionContinuation(source, 'experience', pageIndex);
    const skillsContinued = isSectionContinuation(source, 'skills', pageIndex);
    const langContinued = isSectionContinuation(source, 'languages', pageIndex);
    const refContinued = isSectionContinuation(source, 'references', pageIndex);

    const phone = data.personalInfo.phone || (showPlaceholders ? '+225 05 00 00 00 00' : '');
    const email = data.personalInfo.email || (showPlaceholders ? 'pro@email.ci' : '');
    const address = data.personalInfo.address || (showPlaceholders ? 'Plateau, Abidjan' : '');
    const website = data.personalInfo.website || (showPlaceholders ? 'linkedin.com/in/exemple' : '');
    const summary =
        data.personalInfo.summary ||
        (showPlaceholders
            ? 'Cadre dynamique avec une experience confirmee en environnement corporate a Abidjan.'
            : '');
    const photoSrc = data.personalInfo.profileImage || '';

    const skills: any[] =
        data.skills?.length
            ? data.skills
            : showPlaceholders
              ? [{ name: 'Negociation' }, { name: 'CRM' }, { name: 'Leadership' }]
              : [];
    const languages: any[] =
        data.languages?.length
            ? data.languages
            : showPlaceholders
              ? [{ name: 'Francais' }, { name: 'Anglais' }]
              : [];
    const references: any[] =
        data.references?.length
            ? data.references
            : showPlaceholders
              ? [{ name: 'M. Diallo', position: 'Directeur', company: 'SA', phone: '+225…' }]
              : [];
    const experience: any[] =
        data.experience?.length
            ? data.experience
            : showPlaceholders
              ? [
                    {
                        position: 'Commercial',
                        company: 'Orange CI',
                        startDate: '2020',
                        endDate: '2024',
                        description: 'Developpement du portefeuille clients B2B.',
                        isCurrent: false,
                    },
                ]
              : [];
    const education: any[] =
        data.education?.length
            ? data.education
            : showPlaceholders
              ? [
                    {
                        degree: 'Master',
                        field: 'Marketing',
                        institution: 'INPHB',
                        startDate: '2016',
                        endDate: '2018',
                        isCurrent: false,
                    },
                ]
              : [];

    return (
        <div
            className={`shadow-2xl cv-page-sheet h-[297mm] max-h-[297mm] w-full flex flex-col overflow-hidden ${fontFamily}`}
            style={{ backgroundColor, color: textColor }}
        >
            {!isContinuationPage ? (
                <header className="px-10 py-7 text-white relative" style={{ backgroundColor: primaryColor }}>
                    <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: accent }} />
                    <div className="flex gap-6 items-center">
                        {(photoSrc || showPlaceholders) && (
                            <div className="w-24 h-24 rounded-lg overflow-hidden bg-white/10 shrink-0 border-2 border-white/30">
                                {photoSrc ? (
                                    <img src={photoSrc} alt="" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full bg-slate-600" />
                                )}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <h1 className="cv-fs-name text-3xl font-bold tracking-tight">
                                {data.personalInfo.firstName || (showPlaceholders ? 'Koffi' : '')}{' '}
                                {data.personalInfo.lastName || (showPlaceholders ? 'NGuessan' : '')}
                            </h1>
                            <p className="text-sm uppercase tracking-widest mt-1 opacity-90">
                                {data.personalInfo.profession ||
                                    (showPlaceholders ? 'Responsable commercial' : '')}
                            </p>
                            <div className="cv-fs-contact flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs opacity-90">
                                {phone && (
                                    <span className="inline-flex items-center gap-1">
                                        <Phone className="w-3 h-3" /> {phone}
                                    </span>
                                )}
                                {email && (
                                    <span className="inline-flex items-center gap-1">
                                        <Mail className="w-3 h-3" /> {email}
                                    </span>
                                )}
                                {address && (
                                    <span className="inline-flex items-center gap-1">
                                        <MapPin className="w-3 h-3" /> {address}
                                    </span>
                                )}
                                {website && (
                                    <span className="inline-flex items-center gap-1">
                                        <Globe className="w-3 h-3" /> {website}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </header>
            ) : (
                <div className="px-10 py-3 border-b" style={{ borderColor: accent }}>
                    <p className="cv-fs-name text-sm font-bold" style={{ color: primaryColor }}>
                        {data.personalInfo.firstName} {data.personalInfo.lastName}
                        <span className="ml-2 text-xs font-normal text-slate-400">{CV_LABELS.suite}</span>
                    </p>
                </div>
            )}

            <div className="flex flex-1 overflow-hidden">
                <div
                    data-cv-sidebar
                    className="w-[32%] p-7 overflow-hidden"
                    style={{ backgroundColor: sectionBackgroundColor }}
                >
                    {!isContinuationPage && summary && (
                        <section className="mb-5">
                            <SectionHeading title="Profil" primaryColor={primaryColor} variant="bar" />
                            <p className="cv-fs-summary text-xs leading-relaxed mt-2 text-slate-600">
                                {summary}
                            </p>
                        </section>
                    )}

                    <section className="mb-5">
                        <SectionHeading
                            title={CV_LABELS.skills}
                            isContinuation={skillsContinued}
                            primaryColor={primaryColor}
                            variant="bar"
                        />
                        <div className="cv-fs-skills space-y-2 mt-2">
                            {skills.map((s, i) => (
                                <div
                                    key={s.id || i}
                                    data-cv-block
                                    data-cv-item-id={s.id || undefined}
                                    className="text-xs px-2 py-1 rounded border-l-2 bg-white"
                                    style={{ borderColor: accent }}
                                >
                                    {s.name}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="mb-5">
                        <SectionHeading
                            title={CV_LABELS.languages}
                            isContinuation={langContinued}
                            primaryColor={primaryColor}
                            variant="bar"
                        />
                        <div className="cv-fs-languages space-y-1 mt-2">
                            {languages.map((l, i) => (
                                <div
                                    key={l.id || i}
                                    data-cv-block
                                    data-cv-item-id={l.id || undefined}
                                    className="text-xs"
                                >
                                    {l.name}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <SectionHeading
                            title={CV_LABELS.references}
                            isContinuation={refContinued}
                            primaryColor={primaryColor}
                            variant="bar"
                        />
                        <div className="cv-fs-references space-y-3 mt-2">
                            {references.map((r, i) => (
                                <div
                                    key={r.id || i}
                                    data-cv-block
                                    data-cv-item-id={r.id || undefined}
                                    className="text-xs"
                                >
                                    <p className="font-bold">{r.name}</p>
                                    <p className="text-slate-500">
                                        {r.position}
                                        {r.company ? ` — ${r.company}` : ''}
                                    </p>
                                    {r.phone && <p>{r.phone}</p>}
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                <div data-cv-main className="flex-1 p-7 overflow-hidden">
                    <section className="mb-6">
                        <SectionHeading
                            title={CV_LABELS.experience}
                            isContinuation={expContinued}
                            primaryColor={primaryColor}
                            variant="bar"
                        />
                        <div className="cv-fs-experience space-y-4 mt-3">
                            {experience.map((exp, i) => (
                                <div
                                    key={exp.id || i}
                                    data-cv-block
                                    data-cv-item-id={exp.id || undefined}
                                    className="pl-3 border-l-2"
                                    style={{ borderColor: accent }}
                                >
                                    <div className="flex justify-between gap-2">
                                        <p className="font-bold text-sm">{exp.position}</p>
                                        <p className="text-[10px] text-slate-500">
                                            {exp.startDate} — {frPresent(!!exp.isCurrent, exp.endDate || '')}
                                        </p>
                                    </div>
                                    <p className="text-xs font-semibold" style={{ color: primaryColor }}>
                                        {exp.company}
                                    </p>
                                    {exp.description && (
                                        <p className="text-xs mt-1 text-slate-600">{exp.description}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>

                    <section>
                        <SectionHeading
                            title={CV_LABELS.education}
                            isContinuation={eduContinued}
                            primaryColor={primaryColor}
                            variant="bar"
                        />
                        <div className="cv-fs-education space-y-3 mt-3">
                            {education.map((edu, i) => (
                                <div key={edu.id || i} data-cv-block data-cv-item-id={edu.id || undefined}>
                                    <p className="font-bold text-sm">
                                        {edu.degree}
                                        {edu.field ? ` — ${edu.field}` : ''}
                                    </p>
                                    <p className="text-xs" style={{ color: primaryColor }}>
                                        {edu.institution}
                                    </p>
                                    <p className="text-[10px] text-slate-500">
                                        {edu.startDate} — {frPresent(!!edu.isCurrent, edu.endDate || '')}
                                    </p>
                                    {edu.description && (
                                        <p className="text-xs mt-1 text-slate-600">{edu.description}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default AbidjanCorporateTemplate;
